// Paste an efmodel document (for example an answer from claude.ai) as a new sketch.
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { openSketch } from '../state/dock';
import { importSketch } from '../state/sketches';
import { readSketchText } from './files';

const usePaste = create<{ open: boolean }>(() => ({ open: false }));

export const openPasteDialog = () => usePaste.setState({ open: true });

/** Finds the JSON object in pasted text, which may be wrapped in a Markdown code block or prose. */
export function extractJson(text: string): string {
  const fenced = /```(?:json)?\s*([\s\S]*?)```/.exec(text);
  const body = fenced ? fenced[1]! : text;
  const start = body.indexOf('{');
  const end = body.lastIndexOf('}');
  return start >= 0 && end > start ? body.slice(start, end + 1) : body;
}

export function PasteDialog() {
  const open = usePaste((s) => s.open);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setText('');
    navigator.clipboard?.readText?.().then(
      (clip) => {
        if (clip.includes('"efmodel"')) setText(clip);
      },
      () => undefined,
    );
  }, [open]);

  const submit = async () => {
    const read = readSketchText(extractJson(text));
    if ('error' in read) {
      setError(read.error);
      return;
    }
    const record = await importSketch(read.doc);
    usePaste.setState({ open: false });
    if (record) openSketch(record.id);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => usePaste.setState({ open: o })}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <Dialog.Content className="fixed top-[12vh] left-1/2 z-50 flex w-[min(640px,92vw)] -translate-x-1/2 flex-col gap-3 rounded-xl border border-border-strong bg-panel-2 p-4 shadow-[var(--shadow-pop)]">
          <Dialog.Title className="text-[14px] font-semibold">Paste a sketch</Dialog.Title>
          <Dialog.Description className="text-[12.5px] text-muted">
            Paste an efmodel document, for example from an agent or a chat answer. Text around the JSON is ignored.
          </Dialog.Description>
          <textarea
            autoFocus
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
            aria-label="efmodel JSON"
            placeholder='{ "format": "efmodel", "formatVersion": "1.0", "title": "…", "model": { … } }'
            className="h-64 w-full resize-y rounded-md border border-border bg-bg p-2 font-mono text-[12px] outline-none focus:border-accent"
          />
          {error && <p className="text-[12.5px] text-removed">{error}</p>}
          <div className="flex justify-end gap-2">
            <Dialog.Close className="h-8 rounded-md border border-border px-3 text-[12.5px] hover:bg-hover">Cancel</Dialog.Close>
            <button disabled={!text.trim()} onClick={() => void submit()} className="h-8 rounded-md bg-accent px-3 text-[12.5px] font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-40">
              Add sketch
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
